import { ChangeDetectionStrategy, Component, ViewChild, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';
import { Option } from '@interfaces/common/option';
import { StandardAccountDictionaryProposal } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import { ApplyStandardAccountDictionaryProposalRequest } from '@interfaces/accounting-data-standardization/requests/standard-account-dictionary.request';
import {
  proposalKindLabel,
  statementTypeLabel,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';

const DIALOG_KEY = 'ads-dictionary-proposal-apply';

/** Limites do contrato: texto da proposta e nota da decisão. */
const TEXT_MAX_LENGTH = 1000;
const NOTE_MAX_LENGTH = 1000;

/**
 * "Editar e aplicar" de uma proposta do revisor (PBI 3423, DPA-DIC-DB): a pessoa ajusta o texto (e, em "NÃO entra", o
 * destino, escolhido entre as contas do mesmo demonstrativo) e pode deixar uma nota. `open()` devolve o corpo do
 * `POST .../apply` ou `null` quando cancela. Destino não trocado vai sem `goesTo`, e o backend mantém o do revisor.
 */
@Component({
  selector: 'app-dictionary-proposal-apply-dialog',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './dictionary-proposal-apply-dialog.component.html',
  styleUrl: './dictionary-proposal-apply-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryProposalApplyDialogComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef?: DialogComponent;

  protected readonly dialogKey = DIALOG_KEY;
  protected readonly textMaxLength = TEXT_MAX_LENGTH;
  protected readonly noteMaxLength = NOTE_MAX_LENGTH;

  protected readonly proposal = signal<StandardAccountDictionaryProposal | null>(null);
  protected readonly destinationOptions = signal<Option[]>([]);
  protected readonly text = signal('');
  protected readonly destinationId = signal<string | null>(null);
  protected readonly note = signal('');

  protected readonly isExclude = computed(() => this.proposal()?.kind === 'Exclude');
  protected readonly kindLabel = computed(() => {
    const proposal = this.proposal();
    return proposal ? proposalKindLabel(proposal.kind) : '';
  });
  protected readonly statementLabel = computed(() =>
    statementTypeLabel(this.proposal()?.statementType)
  );
  protected readonly textLabel = computed(() =>
    this.proposal()?.kind === 'Definition' ? 'Definição nova' : 'Texto a acrescentar'
  );

  private resolver: ((body: ApplyStandardAccountDictionaryProposalRequest | null) => void) | null =
    null;

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  /** `destinationOptions`: contas do mesmo demonstrativo (value = `standardAccountId`), só usadas em "NÃO entra". */
  open(
    proposal: StandardAccountDictionaryProposal,
    destinationOptions: Option[]
  ): Promise<ApplyStandardAccountDictionaryProposalRequest | null> {
    this.resolve(null);
    this.proposal.set(proposal);
    this.text.set(proposal.text ?? '');
    this.note.set('');
    this.destinationId.set(proposal.goesToStandardAccountId ?? null);
    this.destinationOptions.set(withCurrentDestination(destinationOptions, proposal));
    return new Promise(resolve => {
      this.resolver = resolve;
      void this.dialogRef
        ?.open('', '', '', 'Cancelar', '', DIALOG_KEY, false, false)
        .then(() => this.resolve(null));
    });
  }

  onDialogClosed(): void {
    this.resolve(null);
  }

  close(): void {
    this.dialogRef?.close();
  }

  isFormValid(): boolean {
    const text = this.text().trim();
    if (text.length === 0 || text.length > TEXT_MAX_LENGTH) return false;
    if (this.note().trim().length > NOTE_MAX_LENGTH) return false;
    return !this.isExclude() || !!this.destinationId();
  }

  submitAction(): void {
    const proposal = this.proposal();
    if (!proposal || !this.isFormValid()) return;

    const body: ApplyStandardAccountDictionaryProposalRequest = {
      text: this.text().trim(),
      note: this.note().trim() || null,
    };
    const destinationId = this.destinationId();
    if (this.isExclude() && destinationId && destinationId !== proposal.goesToStandardAccountId) {
      const option = this.destinationOptions().find(item => item.value === destinationId);
      body.goesToStandardAccountId = destinationId;
      body.goesTo = (option?.data as string | undefined) ?? option?.label ?? null;
    }
    this.resolve(body);
    this.close();
  }

  private resolve(body: ApplyStandardAccountDictionaryProposalRequest | null): void {
    const resolver = this.resolver;
    this.resolver = null;
    resolver?.(body);
  }
}

/** Garante que o destino que o revisor escolheu aparece na lista, mesmo fora das opções carregadas. */
function withCurrentDestination(
  options: Option[],
  proposal: StandardAccountDictionaryProposal
): Option[] {
  const currentId = proposal.goesToStandardAccountId;
  if (!currentId || options.some(option => option.value === currentId)) return options;
  const label = proposal.goesTo?.trim() || 'Destino proposto pelo revisor';
  return [{ value: currentId, label, data: label }, ...options];
}
